import React, { useEffect } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { type PreloadedQuery, graphql, usePreloadedQuery } from 'react-relay';
import BreadcrumbRenderer from '@atlassian/jira-breadcrumbs/src/breadcrumb-renderer/index.tsx';
import ProjectBreadcrumb from '@atlassian/jira-breadcrumbs/src/common-breadcrumbs/project/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import type { mainArcivedIssuesProjectNameQuery } from '@atlassian/jira-relay/src/__generated__/mainArcivedIssuesProjectNameQuery.graphql';
import { useArchiveIssueActions } from '../../../controllers/main.tsx';

type ProjectBreadcrumbWithDataProps = {
	projectKey: string;
	queryReference: PreloadedQuery<mainArcivedIssuesProjectNameQuery>;
};

type BreadcrumbFallbackProps = {
	projectKey: string;
	projectName: string;
};

export const ArchiveIssueBreadcrumbs = ({
	projectKey,
	queryReference,
}: ProjectBreadcrumbWithDataProps) => (
	<Placeholder
		name="issue-archive-breadcrumbs"
		fallback={<BreadcrumbFallback projectKey={projectKey} projectName="" />}
	>
		<ProjectBreadcrumbWithData queryReference={queryReference} projectKey={projectKey} />
	</Placeholder>
);

const ProjectBreadcrumbWithData = ({
	queryReference,
	projectKey,
}: ProjectBreadcrumbWithDataProps) => {
	const data = usePreloadedQuery<mainArcivedIssuesProjectNameQuery>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			query mainArcivedIssuesProjectNameQuery($projectKey: String!, $cloudId: ID!) {
				jira {
					jiraProjectByKey(key: $projectKey, cloudId: $cloudId) {
						... on JiraProject {
							__typename
							id
							name
						}
					}
				}
			}
		`,
		queryReference,
	);

	const projectName: string = data?.jira?.jiraProjectByKey?.name ?? '';
	const projectId: string = data?.jira?.jiraProjectByKey?.id ?? '';
	const { setProjectId } = useArchiveIssueActions();
	useEffect(() => {
		if (projectId) setProjectId(projectId);
	}, [projectId, setProjectId]);

	return (
		<>
			<ProjectBreadcrumb projectKey={projectKey} projectName={projectName}>
				<BreadcrumbRenderer />
			</ProjectBreadcrumb>
		</>
	);
};

const BreadcrumbFallback = ({ projectKey, projectName }: BreadcrumbFallbackProps) => (
	<ProjectBreadcrumb projectKey={projectKey} projectName={projectName}>
		<BreadcrumbRenderer />
	</ProjectBreadcrumb>
);
